/** Max length accepted by tss-svc-tracking collect batch schema. */
export const MAX_EVENT_ID_LENGTH = 128;

const SCOPED_EVENT_ID_PREFIX = 'te_eid:';

function clipEventId(value: string): string {
  return value.slice(0, MAX_EVENT_ID_LENGTH);
}

function sanitizeScopeSegment(value: string): string {
  return value.trim().replace(/\s+/g, '');
}

function uuidFromRandomBytes(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** UUID v4 for pixel dedup — browser + server + GTM must share the same value per logical fire. */
export function createPixelEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return clipEventId(crypto.randomUUID());
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    return clipEventId(uuidFromRandomBytes());
  }
  return clipEventId(`tfp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
}

function scopedStorageKey(scope: string): string {
  return `${SCOPED_EVENT_ID_PREFIX}${scope}`;
}

function readScopedEventId(scope: string): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const storageKey = scopedStorageKey(scope);
  const fromSession = sessionStorage.getItem(storageKey);
  if (fromSession) return fromSession;

  const cookiePrefix = `${SCOPED_EVENT_ID_PREFIX}${encodeURIComponent(scope)}=`;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const entry of cookies) {
    if (entry.startsWith(cookiePrefix)) {
      return decodeURIComponent(entry.slice(cookiePrefix.length));
    }
  }

  return undefined;
}

function writeScopedEventId(scope: string, eventId: string): void {
  if (typeof window === 'undefined') return;

  const storageKey = scopedStorageKey(scope);
  sessionStorage.setItem(storageKey, eventId);

  try {
    const cookieName = `${SCOPED_EVENT_ID_PREFIX}${encodeURIComponent(scope)}`;
    document.cookie = `${cookieName}=${encodeURIComponent(eventId)}; path=/; max-age=86400; SameSite=Lax`;
  } catch {
    // ignore cookie write failures
  }
}

/**
 * One UUID per logical event. Scoped keys (cart token, order id) reuse the same id
 * across sessionStorage + cookie so layout/checkout origins stay aligned.
 */
export function resolvePixelEventId(scope?: string): string {
  if (scope) {
    const existing = readScopedEventId(scope);
    if (existing) return existing;
    const created = createPixelEventId();
    writeScopedEventId(scope, created);
    return created;
  }

  return createPixelEventId();
}

export function buildPurchaseEventScope(orderId: string): string {
  return `purchase:${sanitizeScopeSegment(orderId)}`;
}

export function buildBeginCheckoutEventScope(cartToken: string): string {
  return `begin_checkout:${sanitizeScopeSegment(cartToken)}`;
}

/** Purchase dedup key — literal order id (Meta CAPI / legacy GTM purchase tags expect this). */
export function buildPurchaseEventId(orderId: string): string {
  return clipEventId(sanitizeScopeSegment(orderId));
}

/** @deprecated Use resolvePixelEventId(buildBeginCheckoutEventScope(cartToken)) */
export function buildBeginCheckoutEventId(cartToken: string): string {
  return resolvePixelEventId(buildBeginCheckoutEventScope(cartToken));
}

declare global {
  interface Window {
    /**
     * Latest golden-funnel event_id — same string as dataLayer.event_id on the last push.
     * GTM should prefer {{DL - event_id}}; legacy HTML tags may read this window mirror.
     * TFP receives event_id via tfp('track', …) — it does not read dataLayer or this field.
     */
    __trafiflowEventId?: string;
    /** @deprecated Use __trafiflowEventId */
    __trafiflowLastEventId?: string;
  }
}

export function publishPixelEventId(eventId: string): void {
  if (typeof window === 'undefined') return;
  window.__trafiflowEventId = eventId;
  window.__trafiflowLastEventId = eventId;
}
